import {
  isStructuredRequirement,
  requirementDataType,
  Rule,
  Requirement,
} from "@/shared/domain/layouts/LayoutRecord";
import {
  CanonicalDataType,
  MessagePathNode,
  TopicNode,
} from "@/shared/domain/topics";
import { pathMatcher } from "@/shared/domain/topics/PathMatcher";
import { TopicData } from "@/shared/state/visualization";

export enum MatchFailure {
  NO_PATH_MATCH = "NO_PATH_MATCH",
  NO_DATATYPE_MATCH = "NO_DATATYPE_MATCH",
  NO_COMPLETE_MATCH = "NO_COMPLETE_MATCH",
  NO_REQUIREMENTS_SPECIFIED = "NO_REQUIREMENTS_SPECIFIED",
}

interface RuleMatch {
  rule: Rule;
  topicData: TopicData[];
}

/**
 * Data types a requirement accepts besides the one it names.
 *
 * Ingestion has typed a list of numbers two ways: older ROS ingestion as
 * `array`, and the SDK's arrow path as the narrower `number_array`. A layout
 * saved on a recording ingested one way meets the other on the next, so each
 * accepts the other, as the service's own layout code already treats them.
 */
const INTERCHANGEABLE_DATA_TYPES: Partial<Record<string, readonly string[]>> = {
  [CanonicalDataType.Array]: [CanonicalDataType.NumberArray],
  [CanonicalDataType.NumberArray]: [CanonicalDataType.Array],
};

/**
 * Why a rule failed to bind. Every failure names the requirement it is about
 * except NO_REQUIREMENTS_SPECIFIED, which is about the rule as a whole and
 * carries null. Splitting the union on `type` lets a reader of the other three
 * take `requirement` without a null check, and a reader of NO_DATATYPE_MATCH
 * take the data type compared without re-deriving it.
 */
export type MatchFailureReason =
  | {
      type: MatchFailure.NO_REQUIREMENTS_SPECIFIED;
      requirement: null;
    }
  | {
      type: MatchFailure.NO_DATATYPE_MATCH;
      requirement: Requirement;
      /**
       * Data type the requirement asked for. Always a string: a requirement
       * that names no data type asks only for a path, so it cannot fail here.
       */
      dataType: string;
    }
  | {
      type: MatchFailure.NO_PATH_MATCH | MatchFailure.NO_COMPLETE_MATCH;
      requirement: Requirement;
    };

interface RuleMatchResult {
  matches: RuleMatch[];
  failures: MatchFailureReason[];
}

class LayoutRuleMatcher {
  /**
   * Binds one layout rule's requirements to the topics in a workspace, as
   * happens when a user applies a layout. A requirement binds to a topic whose
   * path it matches, and whose data type it matches when it names one.
   *
   * All requirements have to bind: if any binds nothing, no match sets come
   * back at all. Otherwise each match set holds one topic per requirement, in
   * requirement order. A failure comes back for every requirement that bound
   * nothing, and for every one that bound more topics than the match sets
   * consumed.
   */
  findMatches(rule: Rule, candidates: TopicNode[]): RuleMatchResult {
    const matches: RuleMatch[] = [];
    const failures: MatchFailureReason[] = [];

    if (rule.requirements.length === 0) {
      failures.push({
        type: MatchFailure.NO_REQUIREMENTS_SPECIFIED,
        requirement: null,
      });
      return { matches, failures };
    }

    const matchesByRequirement = rule.requirements.map((requirement) => {
      const pathMatches = this.evaluateByPath(requirement, candidates);

      if (pathMatches.length === 0) {
        failures.push({ type: MatchFailure.NO_PATH_MATCH, requirement });
        return { requirement, matches: [] };
      }

      // A requirement that names no data type asks only for a path, so every
      // topic the path reached is a match; comparing each against an undefined
      // data type would instead drop them all.
      const dataType = requirementDataType(requirement);
      if (dataType === undefined) {
        return {
          requirement,
          matches: pathMatches.map((match) => match.toTopicData()),
        };
      }

      const typeMatches = this.evaluateByDataType(dataType, pathMatches);
      if (typeMatches.length === 0) {
        failures.push({
          type: MatchFailure.NO_DATATYPE_MATCH,
          requirement,
          dataType,
        });
        return { requirement, matches: [] };
      }

      return {
        requirement,
        matches: typeMatches.map((match) => match.toTopicData()),
      };
    });

    if (
      matchesByRequirement.length === 0 ||
      matchesByRequirement.some((m) => m.matches.length === 0)
    ) {
      return { matches, failures };
    }

    // A rule carries more than one requirement when a panel binds topics
    // together as a unit: a Map path's latitude and longitude, or a 3D
    // entity's forward, right, and up.
    const minMatches = Math.min(
      ...matchesByRequirement.map((m) => m.matches.length),
    );
    for (let i = 0; i < minMatches; i++) {
      matches.push({
        rule,
        topicData: matchesByRequirement.map((m) => m.matches[i]),
      });
    }

    matchesByRequirement.forEach(({ requirement, matches }) => {
      if (matches.length > minMatches) {
        failures.push({ type: MatchFailure.NO_COMPLETE_MATCH, requirement });
      }
    });

    return { matches, failures };
  }

  private evaluateByDataType(
    dataType: string,
    candidates: MessagePathNode[],
  ): MessagePathNode[] {
    const accepted = [
      dataType,
      ...(INTERCHANGEABLE_DATA_TYPES[dataType] ?? []),
    ];
    return candidates.filter((candidate) => {
      // A stored layout can name a data type this application does not know,
      // so the requirement side is any string; widening the candidate's enum
      // lets the two compare.
      const canonicalDataType: string = candidate.data.canonical_data_type;
      return accepted.includes(canonicalDataType);
    });
  }

  private evaluateByPath(
    requirement: Requirement,
    candidates: TopicNode[],
  ): MessagePathNode[] {
    if (isStructuredRequirement(requirement)) {
      return pathMatcher.findNodesByParts(
        requirement.topicName,
        requirement.messagePath,
        candidates,
      );
    }

    return pathMatcher.findNodesByJoinedPath(
      requirement.topicMessagePath,
      candidates,
    );
  }
}

export const layoutRuleMatcher = new LayoutRuleMatcher();
